import { Graphics, Container, ContainerChild, Text, TextStyle } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetCountdown,
} from "../../../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { TimerService } from "../../../Services/TimerService";

import { calculateSimpleString } from "../Countdown.utils";

import type { ICountdownTheme } from "./ICountdownTheme";

import { ensureFontReady } from "../../../../../../hooks/useGoogleFont";

import {
  anchorXForAlign,
  computeLetterSpacingPx,
  computeLineHeightPx,
} from "../../../../../Text/textLayout";

export class SimpleRenderer implements ICountdownTheme {
  private timerCallback: ((time: number) => void) | null = null;

  private container: Container | null = null;

  render(
    widget: IStudioWidget,

    parentContainer: Container,

    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const options = (widget as IStudioWidgetCountdown).options;

    this.container = new Container();

    parentContainer.addChild(this.container);

    const backgroundColor = options.backgroundColor;

    if (backgroundColor && backgroundColor !== "transparent") {
      const bg = new Graphics();

      bg.rect(0, 0, destination.width, destination.height).fill(
        backgroundColor,
      );

      this.container.addChild(bg);
    }

    const targetDate = options.targetDate
      ? new Date(options.targetDate)
      : new Date();

    const fontSize = options.fontSize ? parseInt(options.fontSize) : 24;

    const fontFamily = options.fontFamily || "Arial";

    const color = options.color || "#FFFFFF";

    const bold = options.bold !== false; // historical default

    const italic = Boolean(options.italic);

    const align = options.textAlign ?? "center";

    const lineHeightPx = computeLineHeightPx(fontSize, options.lineHeight ?? 1);

    const letterSpacingPx = computeLetterSpacingPx(
      options.wordSpacing,

      fontSize,
    );

    const buildStyle = () =>
      new TextStyle({
        fontFamily,

        fontSize,

        fill: color,

        align,

        fontWeight: bold ? "bold" : "normal",

        fontStyle: italic ? "italic" : "normal",

        lineHeight: lineHeightPx,

        letterSpacing: letterSpacingPx,
      });

    const text = new Text({
      text: calculateSimpleString(targetDate, options),

      style: buildStyle(),
    });

    text.anchor.set(anchorXForAlign(align), 0.5);

    text.x =
      align === "left"
        ? 0
        : align === "right"
          ? destination.width
          : destination.width / 2;

    text.y = destination.height / 2;

    this.container.addChild(text as unknown as ContainerChild);

    // Pixi rasterises text on first paint using whatever font face is

    // available — if the Google Font is still downloading, that means

    // the system fallback. Subsequent timer ticks change the text but

    // hit Pixi's style cache so the fallback sprite stays. Force a fresh

    // TextStyle once the real face is decoded so the cache is busted.

    void ensureFontReady(fontFamily, fontSize, bold ? 700 : 400).then(() => {
      if (!this.container) return;

      text.style = buildStyle();
    });

    const timerService = TimerService.getInstance();

    const update = (_now: number) => {
      const timeString = calculateSimpleString(targetDate, options);

      if (text.text !== timeString) {
        text.text = timeString;
      }
    };

    update(Date.now());

    this.timerCallback = update;

    timerService.subscribe(update);
  }

  destroy(): void {
    if (this.timerCallback) {
      TimerService.getInstance().unsubscribe(this.timerCallback);

      this.timerCallback = null;
    }

    if (this.container) {
      this.container.destroy({ children: true });

      this.container = null;
    }
  }
}
